import React from "react";
import styled from "styled-components";

import {
  fromAWSDate,
  shortLocale,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  useClassroomUnitsManager,
  useQueryGetClassroomAverageByUnit,
  useQueryGetClassroomCourseCalendar,
  useQueryGetCourseUnits,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  HSpace,
  Modal,
  RouterLink,
  Toolbar,
} from "@examspark/common-ui";
import {
  IconAcceptLine,
  IconRefreshLine,
  IconResetLine,
} from "@examspark/common-ui/dist/components/icons";
import { ParsedClassroom } from "@examspark/types";

import UnitStatusBadge from "./units/UnitStatusBadge";

const UnitsList = styled.div`
  display: flex;
  flex-direction: column;
`;
const UnitsListItem = styled.div`
  display: flex;
  align-items: top;
  gap: 2rem;
  border-top: solid 1px #f2f2f2;
  padding: 1.5rem 0;
  margin-top: 1rem;

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;
const UnitNumberLabel = styled.div`
  font-size: 1.1rem;
  color: #777;
`;
const ListItemMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  a {
    font-size: 1.3rem;
  }
`;
const ListItemScore = styled.div`
  font-size: 2rem;
  color: #bbb;
  text-align: right;
  margin-right: -1rem;
  padding: 0.3rem 0;
`;
const ListItemActions = styled.div`
  width: 9rem;
`;

const ScoreGood = styled.div`
  color: green;
`;
const ScoreFair = styled.div`
  color: orange;
`;
const ScorePoor = styled.div`
  color: red;
`;
// const ScoreUnavailable = styled.div`
//   color: #ccc;
// `;

export const Score: React.FC<
  React.PropsWithChildren<{
    value?: number;
  }>
> = (props) => {
  if (props.value === undefined) return null;
  // return <ScoreUnavailable>N/A</ScoreUnavailable>;

  const value = props.value * 100;
  const valueFormatted = value.toFixed(0);

  if (value >= 80) return <ScoreGood>{valueFormatted}%</ScoreGood>;
  if (value >= 60) return <ScoreFair>{valueFormatted}%</ScoreFair>;
  return <ScorePoor>{valueFormatted}%</ScorePoor>;
};

/** @deprecated Moving to ClassroomUnitsManager in `common-app` */
// ^^^ That component does not have pacing, so we cannot move over - travis;
const ClassroomUnits = (props: {
  classroom: ParsedClassroom;
  onUpdate?: () => void;
}) => {
  const { handleFinish, handleSkip, handleStart, handleReset } =
    useClassroomUnitsManager(props.classroom);
  const { data: scores } = useQueryGetClassroomAverageByUnit(
    props.classroom.id,
  );
  const { data: courseUnits } = useQueryGetCourseUnits(
    props.classroom.courseId,
  );
  const activeClassroomUnit = props.classroom.units?.find(
    (u) => u.maturity === "IN_PROGRESS",
  );

  const courseCalendarQuery = useQueryGetClassroomCourseCalendar(
    props.classroom,
  );

  const pacingByUnit = courseCalendarQuery.data;

  return (
    <UnitsList>
      {courseUnits?.map((unit, unitIdx) => {
        const unitId = unit.id;
        const classroomUnit = props.classroom.units?.find(
          (u) => u.unit?.id === unitId,
        );
        const unitPlan = pacingByUnit?.units?.find(({ id }) => id === unitId);
        const unitIsConcluded = ["COMPLETE", "SKIPPED"].includes(
          classroomUnit?.maturity ?? "",
        );

        const unitPlanStartDate = unitPlan?.dates?.at(0);
        const unitPlanEndDate = unitPlan?.dates?.at(-1);
        const isCurrentUnit = activeClassroomUnit?.unit?.id === unitId;
        const hasActiveUnit = !!props.classroom.unitId;

        return (
          <UnitsListItem key={unitId}>
            <ListItemMain>
              <div>
                <RouterLink
                  to={`/classroom/${props.classroom.id}/unit/${unit.id}`}
                >
                  {unit.name}
                </RouterLink>
              </div>
              <HSpace>
                <UnitStatusBadge classroomUnit={classroomUnit} />
                {!!unitPlanStartDate && !!unitPlanEndDate && (
                  <span>
                    {shortLocale.format(fromAWSDate(unitPlanStartDate)) +
                      " - " +
                      shortLocale.format(fromAWSDate(unitPlanEndDate))}
                  </span>
                )}
              </HSpace>
              <UnitNumberLabel>
                Unit / Module {unit.numberLabel ?? unitIdx + 1}
              </UnitNumberLabel>
            </ListItemMain>
            <ListItemScore>
              <Score value={scores?.[unitId]} />
            </ListItemScore>
            <ListItemActions>
              {unitIsConcluded ? (
                <Toolbar>
                  <Button
                    stretch
                    disabled={
                      props.classroom.isArchived ||
                      (hasActiveUnit && !isCurrentUnit)
                    }
                    variant="outlined"
                    color="secondary"
                    title="Resume"
                    icon={
                      <svg x="0px" y="0px" viewBox="0 0 16 16">
                        <polygon points="13.7,8 2.5,1.6 2.5,14.5 " />
                      </svg>
                    }
                    onClick={async () => {
                      await ConfirmModal.show({
                        onResolving: async (result) => {
                          if (result) {
                            await handleStart(unitId);
                          }
                        },
                        children: (
                          <Modal.PaddedBody>
                            <CautionBanner variant="plain">
                              Are you sure you want to resume this unit /
                              module?
                            </CautionBanner>
                          </Modal.PaddedBody>
                        ),
                      });
                    }}
                  />
                  <Button
                    stretch
                    disabled={!!props.classroom.isArchived}
                    variant="outlined"
                    title="Reset"
                    icon={<IconResetLine />}
                    onClick={async () => {
                      await ConfirmModal.show({
                        confirmLabel: "Reset Unit",
                        onResolving: async (result) => {
                          if (result) {
                            await handleReset(unitId);
                          }
                        },
                        children: (
                          <Modal.PaddedBody>
                            <CautionBanner variant="plain">
                              Are you sure you want to reset this unit / module?
                            </CautionBanner>
                          </Modal.PaddedBody>
                        ),
                      });
                    }}
                  />
                </Toolbar>
              ) : (
                <>
                  {isCurrentUnit ? (
                    <Toolbar>
                      <Button
                        stretch
                        disabled={!!props.classroom.isArchived}
                        variant="contained"
                        color={!unitIsConcluded ? "create" : "secondary"}
                        title="Finish"
                        icon={<IconAcceptLine />}
                        onClick={async () => {
                          await ConfirmModal.show({
                            onResolving: async (result) => {
                              if (result) {
                                await handleFinish(unitId);
                              }
                            },
                            children: (
                              <Modal.PaddedBody>
                                <CautionBanner variant="plain">
                                  Are you sure you want to mark this unit /
                                  module complete?
                                </CautionBanner>
                              </Modal.PaddedBody>
                            ),
                          });
                        }}
                      />
                      <Button
                        stretch
                        disabled={!!props.classroom.isArchived}
                        variant="outlined"
                        title="Reset"
                        icon={<IconResetLine />}
                        onClick={async () => {
                          await ConfirmModal.show({
                            confirmLabel: "Reset Unit",
                            onResolving: async (result) => {
                              if (result) {
                                await handleReset(unitId);
                              }
                            },
                            children: (
                              <Modal.PaddedBody>
                                <CautionBanner variant="plain">
                                  Are you sure you want to reset this unit /
                                  module?
                                </CautionBanner>
                              </Modal.PaddedBody>
                            ),
                          });
                        }}
                      />
                    </Toolbar>
                  ) : (
                    <>
                      <Toolbar>
                        <Button
                          stretch
                          variant={unitIsConcluded ? "outlined" : "contained"}
                          icon={
                            unitIsConcluded ? (
                              <IconRefreshLine />
                            ) : (
                              <svg x="0px" y="0px" viewBox="0 0 16 16">
                                <polygon points="13.7,8 2.5,1.6 2.5,14.5 " />
                              </svg>
                            )
                          }
                          disabled={
                            !!props.classroom.isArchived ||
                            (hasActiveUnit && !isCurrentUnit)
                          }
                          color={!unitIsConcluded ? "primary" : "secondary"}
                          title="Start"
                          onClick={async () => {
                            await ConfirmModal.show({
                              onResolving: async (result) => {
                                if (result) {
                                  await handleStart(unitId);
                                }
                              },
                              children: (
                                <Modal.PaddedBody>
                                  <CautionBanner variant="plain">
                                    Are you sure you want to start this unit /
                                    module?
                                  </CautionBanner>
                                </Modal.PaddedBody>
                              ),
                            });
                          }}
                        />
                        {!unitIsConcluded && (
                          <Button
                            stretch
                            disabled={!!props.classroom.isArchived}
                            variant="outlined"
                            title="Skip"
                            icon={
                              <svg x="0px" y="0px" viewBox="0 0 16 16">
                                <g>
                                  <path d="M11.2,8.2l-9-5.5c-0.4-0.2-0.8,0-0.8,0.5v11c0,0.4,0.5,0.7,0.8,0.5l9-5.5C11.6,8.9,11.6,8.4,11.2,8.2z" />
                                  <g>
                                    <g>
                                      <path d="M14,8.5c0,3,0,5.5,0,5.5S14,11.5,14,8.5S14,3,14,3S14,5.5,14,8.5z" />
                                      <rect
                                        x="13.8"
                                        y="3"
                                        width="0.5"
                                        height="11"
                                      />
                                    </g>
                                    <g>
                                      <path d="M14,14.5c-0.3,0-0.5-0.2-0.5-0.5V3c0-0.3,0.2-0.5,0.5-0.5s0.5,0.2,0.5,0.5v11C14.5,14.3,14.3,14.5,14,14.5z" />
                                      <path
                                        d="M14,14.8c-0.4,0-0.8-0.3-0.8-0.8V3c0-0.4,0.3-0.8,0.8-0.8s0.8,0.3,0.8,0.8v11C14.8,14.4,14.4,14.8,14,14.8z M14,2.8
				c-0.1,0-0.2,0.1-0.2,0.2v11c0,0.1,0.1,0.2,0.2,0.2s0.2-0.1,0.2-0.2V3C14.2,2.9,14.1,2.8,14,2.8z"
                                      />
                                    </g>
                                  </g>
                                </g>
                              </svg>
                            }
                            onClick={async () => {
                              await ConfirmModal.show({
                                confirmLabel: "Skip Unit",
                                onResolving: async (result) => {
                                  if (result) {
                                    await handleSkip(unitId);
                                  }
                                },
                                children: (
                                  <Modal.PaddedBody>
                                    <CautionBanner variant="plain">
                                      Are you sure you want to skip this unit /
                                      module?
                                    </CautionBanner>
                                  </Modal.PaddedBody>
                                ),
                              });
                            }}
                          />
                        )}
                      </Toolbar>
                    </>
                  )}
                </>
              )}
            </ListItemActions>
          </UnitsListItem>
        );
      })}
    </UnitsList>
  );
};

export default ClassroomUnits;
